/**
 * `callAttempts/{id}` — one doc per web call attempt (#47), same shape the
 * mobile app writes, so a failed clinic call can be diagnosed afterwards:
 *
 *   {coachId, customerId, roomId, role, platform: 'web', stage, error, at}
 *
 * `stage` is how far the attempt got ('token' → 'connect' → 'connected' →
 * 'ended'); `error` is set when it stopped there because of a failure. The doc
 * is created at the first stage and merged as the attempt progresses, with
 * `at` re-stamped by the server each time.
 *
 * Writing is best-effort: a logging failure must never break the call.
 */

export type CallAttemptStage = 'token' | 'connect' | 'connected' | 'ended';

export interface CallAttemptContext {
  coachId: string;
  customerId: string;
  /** LiveKit room name the attempt is joining. */
  roomId: string;
  role: 'coach' | 'customer';
}

export interface CallAttemptFields extends CallAttemptContext {
  platform: 'web';
  stage: CallAttemptStage;
  error: string | null;
}

export function buildCallAttempt(
  ctx: CallAttemptContext,
  stage: CallAttemptStage,
  error?: string | null
): CallAttemptFields {
  return {
    coachId: ctx.coachId,
    customerId: ctx.customerId,
    roomId: ctx.roomId,
    role: ctx.role,
    platform: 'web',
    stage,
    error: error ? String(error).slice(0, 500) : null,
  };
}

/** Persists one attempt's fields; `at` is added by the writer. */
export type CallAttemptWriter = (attemptId: string, fields: CallAttemptFields) => Promise<void>;

const firestoreWriter: CallAttemptWriter = async (attemptId, fields) => {
  const [{ db }, { doc, setDoc, serverTimestamp }] = await Promise.all([
    import('@/lib/firebase/config'),
    import('firebase/firestore'),
  ]);
  await setDoc(doc(db, 'callAttempts', attemptId), { ...fields, at: serverTimestamp() }, { merge: true });
};

export interface CallAttemptRecorder {
  readonly attemptId: string;
  record: (stage: CallAttemptStage, error?: string | null) => void;
}

function newAttemptId(ctx: CallAttemptContext): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `web-${ctx.role}-${Date.now()}-${rand}`;
}

export function startCallAttempt(
  ctx: CallAttemptContext,
  write: CallAttemptWriter = firestoreWriter
): CallAttemptRecorder {
  const attemptId = newAttemptId(ctx);
  return {
    attemptId,
    record(stage, error) {
      write(attemptId, buildCallAttempt(ctx, stage, error)).catch((err) => {
        console.warn('[callAttempts] write failed:', err);
      });
    },
  };
}
